import { useState, useSyncExternalStore } from 'react'
import type { Degradation, DependencyHealthStore } from '@solum/experience'
import s from './DependencyStatus.module.css'

interface DependencyStatusCommonProps {
  onRetry?: () => void | Promise<unknown>
  retryLabel?: string
  testId?: string
}

export type DependencyStatusProps = DependencyStatusCommonProps & (
  | {
      message: string | null
      source?: never
      messageFor?: never
    }
  | {
      source: DependencyHealthStore
      messageFor: (state: Degradation) => string | null
      message?: never
    }
)

/**
 * One app-level region that names the dependency that is not answering (#1790).
 *
 * SLM-MOONBASE #124: a deployment's data plane was stopped for ten days while
 * the console signed people in and rendered. Every failure surface in the app
 * was — and still is — **per panel**: the home page showed two unrelated error
 * panels, derived from two different queries, unable to know they had failed for
 * the same reason. Two retry buttons over one dead service.
 *
 * So this is deliberately not a fourth error component. `ErrorState` still owns
 * "this panel could not load"; what did not exist is anywhere to say "the data
 * service is not responding" **once**, above the whole application, in the words
 * a person would use.
 *
 * ── it is a control, so it knows nothing ────────────────────────────────────
 * `packages/ui` may not fetch, may not know an entity key and may not import
 * `@solum/sdk` — the portal is always a host with a different data door. The
 * derivation lives in `@solum/experience` (`DependencyHealth`), each host binds
 * its own transport to it, and this renders what it is handed. That is what lets
 * both hosts show the same thing without either importing the other.
 *
 * ── why `role="status"` and not `role="alert"` ──────────────────────────────
 * `alert` interrupts a screen-reader user mid-sentence, and this region appears
 * for as long as a dependency is down — potentially the whole session. An
 * assertive live region that persists is the auditory equivalent of a banner
 * that cannot be dismissed. `status` is polite: it is announced at the next
 * pause, and it is announced AGAIN when the text changes — which is what should
 * happen when the sentence changes from "this device cannot reach the
 * deployment" to naming the service, or back to nothing on recovery. (It is NOT
 * a claim about a second dependency: only `data` is observed today, and a
 * comment here used to promise an announcement the code could not produce.)
 * `ErrorState` keeps `role="alert"` because it replaces content the person was
 * waiting for, which is a different event.
 *
 * ── why the region is MOUNTED while healthy, holding nothing ────────────────
 * The obvious shape — `if (!message) return null` — is the one this component
 * shipped with for a day, and it silently costs the feature its entire point
 * for the people it matters most to. A live region has to be in the accessibility
 * tree BEFORE its text arrives: assistive technology subscribes to changes
 * INSIDE an existing region, and a `role="status"` element inserted together
 * with its own content is announced unreliably — NVDA and JAWS commonly say
 * nothing at all. So a blind operator would have got the one thing this exists
 * to provide, silently, never.
 *
 * The two objections in the comment this replaces were both wrong, and worth
 * writing down because they are the natural instincts:
 *
 *   · "it shifts the layout" — an empty `<div>` with no padding, border or
 *     content occupies zero block size. Only `.region` is styled, and `.region`
 *     is only rendered when there is a sentence in it.
 *   · "an empty live region announces its own emptiness" — it announces on
 *     CHANGE, and nothing has changed. An empty region is silent by definition;
 *     what is not silent is a region that is never announced at all.
 */
export function DependencyStatus(props: DependencyStatusProps) {
  if (props.source) return <ObservedDependencyStatus {...props} />
  return <DependencyStatusView {...props} />
}

function ObservedDependencyStatus({ source, messageFor, ...props }:
  DependencyStatusCommonProps & {
    source: DependencyHealthStore
    messageFor: (state: Degradation) => string | null
  }) {
  const state = useSyncExternalStore(source.subscribe, source.getSnapshot, source.getSnapshot)
  return <DependencyStatusView {...props} message={messageFor(state)} />
}

function DependencyStatusView({
  message, onRetry, retryLabel = 'Try again', testId = 'dependency-status',
}: DependencyStatusCommonProps & { message: string | null }) {
  const [retrying, setRetrying] = useState(false)
  /* `aria-busy` and `disabled` rather than a changed label: the label lives
     INSIDE `aria-live`, so swapping it to "Checking…" would re-read the whole
     sentence to a screen-reader user on every press. `aria-busy` is silent. */
  const retry = onRetry && (() => {
    const running = onRetry()
    if (!running || typeof (running as PromiseLike<unknown>).then !== 'function') return
    setRetrying(true)
    void Promise.resolve(running).finally(() => { setRetrying(false) })
  })
  return (
    <div className={s.live} role="status" aria-live="polite" data-testid={testId}>
      {message
        ? (
          <div className={s.region}>
            <span className={s.glyph} aria-hidden="true">⚠</span>
            <span className={s.message}>{message}</span>
            {retry
              ? (
                <button
                  type="button" className={s.retry} onClick={retry}
                  disabled={retrying} aria-busy={retrying}
                  data-testid={`${testId}-retry`}
                >
                  {retryLabel}
                </button>
              )
              : null}
          </div>
        )
        : null}
    </div>
  )
}
